import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, Video, Smartphone, Wifi, Shield, Eye, AlertCircle, Radio } from "lucide-react";

export default function SecurityAndSurveillance() {
    // useEffect(() => {
    //     if (!sessionStorage.getItem("securityReloaded")) {
    //         sessionStorage.setItem("securityReloaded", "true");
    //         if (performance.getEntriesByType("navigation")[0].type !== "reload") {
    //             window.location.reload();
    //         }
    //     } else {
    //         sessionStorage.removeItem("securityReloaded");
    //     }
    // }, []);
    const { t, i18n } = useTranslation();
    const dir = i18n.language === "ar" ? "rtl" : "ltr";

    const alarmFeatures = [
        { icon: Radio, key: "motionSensors" },
        { icon: Video, key: "realTimeRecording" },
        { icon: Bell, key: "sirens" },
        { icon: Smartphone, key: "mobileApp" },
        { icon: Wifi, key: "connectivity" }
    ];

    return (
        <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
            {/* Hero Section */}
            <section className="relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }}></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
                        {/* Image Side */}
                        <motion.div
                            initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="md:w-1/2"
                        >
                            <div className="relative">
                                <div
                                    className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                                    style={{ background: '#ffde21' }}
                                ></div>
                                <img
                                    src="/images/securityimg.webp"
                                    alt={t("securityAndSurveillanceSystem")}
                                    className="relative rounded-3xl shadow-2xl w-full object-cover"
                                    style={{ border: '3px solid #ffde21' }}
                                    fetchpriority="high"
                                    decoding="async"
                                />
                            </div>
                        </motion.div>

                        {/* Text Side */}
                        <motion.div
                            initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="md:w-1/2"
                        >
                            <div className="inline-block mb-4">
                                <span
                                    className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                                    style={{
                                        background: 'rgba(255, 222, 33, 0.2)',
                                        color: '#2d2d2b'
                                    }}
                                >
                                    <Shield size={16} />
                                    {dir === "rtl" ? "حماية متقدمة" : "Advanced Protection"}
                                </span>
                            </div>

                            <h1
                                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                                style={{ color: '#2d2d2b' }}
                            >
                                {t("securityAndSurveillanceSystem")}
                            </h1>

                            <p
                                className="text-lg md:text-xl leading-relaxed mb-8"
                                style={{ color: '#2d2d2b', opacity: 0.8 }}
                            >
                                {dir === "rtl"
                                    ? "حماية شاملة لمنزلك أو عملك مع أنظمة مراقبة وإنذار متطورة تعمل على مدار الساعة"
                                    : "Comprehensive protection for your home or business with advanced surveillance and alarm systems working 24/7"}
                            </p>

                            <Link to="/design-home">
                                <button
                                    className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                                    style={{
                                        background: '#ffde21',
                                        color: '#2d2d2b'
                                    }}
                                >
                                    {dir === "rtl" ? "احصل على عرض سعر" : "Get a Quote"}
                                </button>
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Alarm Systems Section */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Section Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center justify-center gap-3 mb-4">
                            <AlertCircle size={40} style={{ color: '#ffde21' }} strokeWidth={2.5} />
                            <h2
                                className="text-3xl md:text-4xl font-black"
                                style={{ color: '#2d2d2b' }}
                            >
                                {t("alarmSystems")}
                            </h2>
                        </div>
                        <p
                            className="text-lg max-w-2xl mx-auto"
                            style={{ color: '#2d2d2b', opacity: 0.7 }}
                        >
                            {dir === "rtl"
                                ? "أنظمة إنذار متطورة تكتشف أي حركة مشبوهة وتنبهك فوراً"
                                : "Advanced alarm systems that detect any suspicious movement and alert you instantly"}
                        </p>
                    </motion.div>

                    {/* Alarm Features Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {alarmFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                                    style={{
                                        background: 'white',
                                        border: '2px solid rgba(255, 222, 33, 0.2)'
                                    }}
                                >
                                    {/* Icon Circle */}
                                    <div
                                        className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                                        style={{ background: '#ffde21' }}
                                    >
                                        <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                                    </div>

                                    {/* Feature Description */}
                                    <p
                                        className="text-base leading-relaxed"
                                        style={{ color: '#2d2d2b' }}
                                    >
                                        {t(`alarm_features.${feature.key}`)}
                                    </p>

                                    {/* Decorative Element */}
                                    <div
                                        className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                                        style={{ background: '#ffde21' }}
                                    ></div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Surveillance Cameras Section */}
            <section
                className="py-16 md:py-24 relative overflow-hidden"
                style={{ background: '#2d2d2b' }}
            >
                {/* Decorative Background */}
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
                        backgroundSize: '60px 60px',
                        backgroundPosition: '0 0, 30px 30px'
                    }}></div>
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                        className="flex flex-col md:flex-row items-center gap-12"
                    >
                        {/* Icon/Visual Side */}
                        <div className="md:w-1/2 flex justify-center">
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                                className="relative"
                            >
                                <div
                                    className="absolute inset-0 rounded-full opacity-20 blur-3xl"
                                    style={{ background: '#ffde21' }}
                                ></div>
                                <div
                                    className="relative w-64 h-64 rounded-full flex items-center justify-center"
                                    style={{
                                        background: 'rgba(255, 222, 33, 0.1)',
                                        border: '4px solid #ffde21'
                                    }}
                                >
                                    <Eye size={120} style={{ color: '#ffde21' }} strokeWidth={1.5} />
                                </div>
                            </motion.div>
                        </div>

                        {/* Text Side */}
                        <div className="md:w-1/2">
                            <h2
                                className="text-3xl md:text-4xl font-black mb-6"
                                style={{ color: '#ffde21' }}
                            >
                                {t("surveillanceCamerasWithRecording")}
                            </h2>

                            <div className="space-y-4">
                                <div
                                    className="p-6 rounded-xl"
                                    style={{ background: 'rgba(255, 222, 33, 0.1)' }}
                                >
                                    <h3
                                        className="text-xl font-bold mb-3"
                                        style={{ color: 'white' }}
                                    >
                                        {dir === "rtl" ? "دقة عالية" : "High Resolution"}
                                    </h3>
                                    <p style={{ color: 'white', opacity: 0.9 }}>
                                        {dir === "rtl"
                                            ? "كاميرات بدقة 4K و 8MP لوضوح استثنائي في جميع الظروف"
                                            : "4K and 8MP cameras for exceptional clarity in all conditions"}
                                    </p>
                                </div>

                                <div
                                    className="p-6 rounded-xl"
                                    style={{ background: 'rgba(255, 222, 33, 0.1)' }}
                                >
                                    <h3
                                        className="text-xl font-bold mb-3"
                                        style={{ color: 'white' }}
                                    >
                                        {dir === "rtl" ? "تسجيل ذكي" : "Smart Recording"}
                                    </h3>
                                    <p style={{ color: 'white', opacity: 0.9 }}>
                                        {dir === "rtl"
                                            ? "أجهزة تسجيل متطورة مع تخزين سحابي ومحلي"
                                            : "Advanced recording devices with cloud and local storage"}
                                    </p>
                                </div>

                                <div
                                    className="p-6 rounded-xl"
                                    style={{ background: 'rgba(255, 222, 33, 0.1)' }}
                                >
                                    <h3
                                        className="text-xl font-bold mb-3"
                                        style={{ color: 'white' }}
                                    >
                                        {dir === "rtl" ? "رؤية ليلية" : "Night Vision"}
                                    </h3>
                                    <p style={{ color: 'white', opacity: 0.9 }}>
                                        {dir === "rtl"
                                            ? "مراقبة واضحة حتى في الظلام الدامس"
                                            : "Clear monitoring even in complete darkness"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-16 md:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <Shield size={60} style={{ color: '#ffde21', margin: '0 auto', marginBottom: '1.5rem' }} />
                        <h2
                            className="text-3xl md:text-4xl font-black mb-6"
                            style={{ color: '#2d2d2b' }}
                        >
                            {dir === "rtl" ? "احمِ ما يهمك" : "Protect What Matters"}
                        </h2>
                        <p
                            className="text-lg md:text-xl mb-8"
                            style={{ color: '#2d2d2b', opacity: 0.7 }}
                        >
                            {t("homepage.cta.description")}
                        </p>
                        <Link to="/contact">
                            <button
                                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                                style={{
                                    background: '#ffde21',
                                    color: '#2d2d2b'
                                }}
                            >
                                {t("homepage.cta.button")}
                            </button>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}